import StateObserver, { now, user } from 'src/StateObserver';
import {
  mapFeatureToDialogOrigin,
  openPopupPromise,
} from 'src/lib/popups/popupOpenClose';
import {
  canParticipateOnCurrentRace,
  getActiveRace,
  getActiveRaceIndex,
  getActiveRaceState,
  getRacePosition,
  getRaceRewards,
  getRaceScores,
  getRaceStartTime,
  getUnclaimedRaces,
} from 'src/replicant/match2/races/races.getters';
import { assertNever } from 'src/replicant/utils';
import { openMatch2RewardsPopup } from '../rewards/match2-rewards.utils';
import { getTournament } from '../tournament/match2-tournament.utils';
import { track } from 'src/lib/analytics/utils';
import { RaceConfig } from 'src/replicant/match2/races/races.ruleset';
import { resolvePromoGroupFeature } from 'src/game/promo/promo.launch';

type OriginFeature = 'launch' | 'hud' | 'postLevel';

/**
 * Opens race introduction popup if the user has not joined a race, or open race leaderboard.
 */
export async function openRaceSequence(args: {
  auto?: boolean;
  originFeature: OriginFeature;
}) {
  const auto = !!args?.auto;

  // Update online bots logic before show any rewards or leaderboards
  await StateObserver.invoke.updateMatch2RaceBots();

  if (getUnclaimedRaces(user(), now()).length > 0) {
    await claimRaceRewards();
  }

  if (!canParticipateOnCurrentRace(user(), now())) {
    return;
  }

  const raceState = getActiveRaceState(user(), now());

  // Skip if already auto showed.
  const { id, config } = getActiveRace(now())!;
  const raceIndex = getActiveRaceIndex(user(), now());
  const { lastViewedRaceScore } = user().match2;

  const isSameRace =
    lastViewedRaceScore?.raceId === id &&
    lastViewedRaceScore?.raceIndex === raceIndex;
  if (auto && isSameRace && lastViewedRaceScore?.autoShowed) {
    return;
  }

  // Mark race popup as viewed.
  await StateObserver.invoke.recordMatch2LastViewedScore({ auto });

  // Show race intro if needed.
  if (!raceState) {
    track('RaceIntroOpen', {
      originFeature: args.originFeature,
      type: config.type,
      goal: getAnalyticsRaceGoal(config),
      eventID: id,
      raceSequence: (user().match2.races[id]?.length ?? 0) + 1, // Should be the race we're about to join.
      buruStreakValue: user().match2.buruStreak.value,
    });

    const join = await openPopupPromise('popupMatch2RacesIntro', {
      useClickOutside: true,
      config,
      origin: mapFeatureToDialogOrigin(args.originFeature),
    });

    // Any intro display satisfies the recurring promo for the current race.
    await resolvePromoGroupFeature({
      groupId: 'recurringFeatures',
      itemId: 'raceIntro',
    });

    if (join) {
      // Get the latest active race after popup is closed, to ensure we have the correct data.
      // This is to handle the edge case where the intro popup is opened just before the previous race concludes.
      const race = getActiveRace(now());

      // Exit prematurely if no active race is found.
      if (!race) return;

      const { id, config } = race;

      await StateObserver.invoke.joinMatch2Race();

      track('RaceStart', {
        type: config.type,
        goal: getAnalyticsRaceGoal(config),
        eventID: id,
        buruStreakValue: user().match2.buruStreak.value,
        tournamentPosition: getTournament()?.getCached()?.currentRank,
        raceSequence: user().match2.races[id].length,
      });
      return openRaceSequence({ originFeature: args.originFeature });
    }

    return;
  }

  // Otherwise show current race's leaderboard.
  await openRaceLeaderboard({ originFeature: args.originFeature });

  // A leaderboard display also satisfies the recurring promo for this race.
  await resolvePromoGroupFeature({
    groupId: 'recurringFeatures',
    itemId: 'raceIntro',
  });
}

/**
 * Claim race rewards.
 */
export async function claimRaceRewards() {
  for (const [raceId, raceIndex] of getUnclaimedRaces(user(), now())) {
    await openRaceLeaderboard({
      raceId,
      raceIndex,
    });

    const { config } = getActiveRace(getRaceStartTime(raceId))!;
    const rewards = getRaceRewards(user(), raceId, raceIndex, now());

    const playerScore = user().match2.races[raceId]?.[raceIndex]?.score ?? 0;
    const scores = getRaceScores(user(), raceId, raceIndex, now());

    track('RaceFinish', {
      type: config.type,
      goal: getAnalyticsRaceGoal(config),
      eventID: raceId,
      buruStreakValue: user().match2.buruStreak.value,
      tournamentPosition: getTournament()?.getCached()?.currentRank,
      position: getRacePosition(user(), raceId, raceIndex, now()),
      finalScore: playerScore,
      ratioWinningScore: playerScore / scores[0],
      raceSequence: raceIndex + 1,
    });

    await StateObserver.invoke.claimMatch2RaceRewards({
      raceId,
      raceIndex,
    });
    if (rewards.length !== 0)
      await openMatch2RewardsPopup({ rewards, origin: undefined });
  }
}

/**
 * Open race leaderboard.
 */
async function openRaceLeaderboard(
  args: {
    raceId?: string;
    raceIndex?: number;
    originFeature?: OriginFeature;
  } = {},
) {
  // Update online bots logic before show any rewards or leaderboards
  await StateObserver.invoke.updateMatch2RaceBots();

  const raceId = args.raceId ?? getActiveRace(now())?.id ?? '';
  const origin = mapFeatureToDialogOrigin(args.originFeature);

  const { config } = getActiveRace(getRaceStartTime(raceId))!;
  switch (config.type) {
    case 'target':
      await openPopupPromise('popupMatch2TargetRacesLeaderboard', {
        useClickOutside: true,
        raceId: args?.raceId,
        raceIndex: args?.raceIndex,
        origin,
      });
      break;
    case 'time':
      await openPopupPromise('popupMatch2TimedRacesLeaderboard', {
        useClickOutside: true,
        raceId: args?.raceId,
        raceIndex: args?.raceIndex,
        origin,
      });
      break;
    default:
      assertNever(config);
  }
}

export function getAnalyticsRaceGoal(
  config: RaceConfig,
): 'fixedDurationLollipops' | 'fixedDurationLevels' | 'fixedTargetLevels' {
  if (config.type === 'target') {
    return 'fixedTargetLevels';
  }
  return config.goal === 'lollipop'
    ? 'fixedDurationLollipops'
    : 'fixedDurationLevels';
}
